// This component is used by both the CreateTask and EditTask page and allows us to have separate routes for the two pages
// but make sure they both stay consistent with any visual or functional changes
import React, { useMemo, useState } from "react";
import Input from "../input";
import LabelContainer from "../labels/LabelContainer";
import LabelsDialog from "../labels/LabelsDialog";
import { Box, Collapse, Grid, IconButton, Typography } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import EventIcon from "@mui/icons-material/Event";
import ScheduleIcon from "@mui/icons-material/Schedule";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import DeadlineContainer from "./taskInputs/DeadlineContainer";
import TargetToggleContainer from "./taskInputs/TargetToggleContainer";
import AttributesContainer from "./taskInputs/AttributesContainer";
import NotesContainer from "./taskInputs/notes/NotesContainer";
import RecurringTasksContainer from "./taskInputs/recurringTasks/RecurringTasksContainer";
import { EmojiPicker } from "../pickers";
import ScheduleContainer from "./taskInputs/ScheduleContainer";
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import { pluralise } from "../../helpers/text";

dayjs.extend(isToday);

// TODO find best way to get rid of gigaprops - It's gonna be ContextAPI
const TaskDataContainer = ({
  name,
  onNameChange,
  nameError,
  nameHelperText,
  emoji,
  setEmoji,
  labels,
  onLabelsChange,
  onLabelRemoved,
  deadline,
  setHasDeadline,
  hasDeadline,
  setDeadline,
  isRecurring,
  setRecurring,
  frequency,
  setFrequency,
  schedule,
  setSchedule,
  targeted,
  setTargeted,
  attributes,
  onAttributeChange,
  notes,
  onAddNote,
  onNoteStatusChange,
  onNoteDeleted,
  showEditLabelsPanel,
  onChangeEditLabelPanelVisibility,
  noteInputValueRef,
  noteInputError,
  onNoteInputErrorChange,
}) => {
  const [timingOpen, setTimingOpen] = useState(false);

  const timingSummaryChips = useMemo(() => {
    const chips = [];
    if (hasDeadline && deadline) {
      const d = dayjs(deadline);
      const fmt = d.isToday()
        ? `Today ${d.format("HH:mm")}`
        : d.format("D MMM HH:mm");
      chips.push({
        key: "deadline",
        type: "Deadline",
        icon: <EventIcon sx={{ fontSize: "14px !important" }} />,
        label: fmt,
      });
    }
    if (schedule) {
      const s = dayjs(
        typeof schedule === "number" ? dayjs.unix(schedule) : schedule,
      );
      const fmt = s.isToday()
        ? `Today ${s.format("HH:mm")}`
        : s.format("D MMM HH:mm");
      chips.push({
        key: "schedule",
        type: "Scheduled",
        icon: <ScheduleIcon sx={{ fontSize: "14px !important" }} />,
        label: fmt,
      });
    }
    if (isRecurring && frequency) {
      const { intervalCount, intervalDateType, startIntervalOnCompletion } =
        frequency;
      const label = startIntervalOnCompletion
        ? `${intervalCount} ${pluralise(intervalDateType, intervalCount)} after done`
        : `Every ${intervalCount === 1 ? "" : `${intervalCount} `}${pluralise(intervalDateType, intervalCount)}`;
      chips.push({
        key: "recurring",
        type: "Repeats",
        icon: <EventRepeatIcon sx={{ fontSize: "14px !important" }} />,
        label,
      });
    }
    return chips;
  }, [hasDeadline, deadline, schedule, isRecurring, frequency]);

  return (
    <>
      <Grid container className="task-data-container__contents scroller">
        <div className="task-data-container__name-container">
          <EmojiPicker emoji={emoji} setEmoji={setEmoji} />
          <Input
            placeholder="Task Name"
            labelText="Task Name"
            onChange={onNameChange}
            value={name}
            error={nameError}
            helperText={nameHelperText}
          />
          <TargetToggleContainer
            targeted={targeted}
            setTargeted={setTargeted}
          />
        </div>
        <Grid container>
          <AttributesContainer
            attributes={attributes}
            onAttributeChange={onAttributeChange}
          />
        </Grid>
        <LabelContainer
          editPanelIsOpen={showEditLabelsPanel}
          selectedLabels={labels}
          onChangeEditLabelPanelVisibility={onChangeEditLabelPanelVisibility}
        />
        <Grid container>
          <div className="task-timing-group">
            <Box
              className="task-timing-group__header"
              onClick={() => setTimingOpen((o) => !o)}
            >
              <Box className="task-timing-group__icon-box">
                <AccessTimeIcon sx={{ color: "#fff", fontSize: "20px" }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "#333", lineHeight: 1.2 }}
                >
                  Timing
                </Typography>
                {!timingOpen && timingSummaryChips.length === 0 && (
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(0,0,0,0.5)" }}
                  >
                    Schedule, deadline &amp; recurrence
                  </Typography>
                )}
              </Box>
              {!timingOpen && timingSummaryChips.length > 0 ? (
                <Box className="task-timing-group__summary-chips">
                  {timingSummaryChips.map((chip) => (
                    <Box
                      key={chip.key}
                      className="task-timing-group__summary-chip"
                    >
                      <Box className="task-timing-group__summary-chip-type">
                        {chip.icon}
                        <span>{chip.type}</span>
                      </Box>
                      <span className="task-timing-group__summary-chip-value">
                        {chip.label}
                      </span>
                    </Box>
                  ))}
                </Box>
              ) : !timingOpen ? (
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(0,0,0,0.5)", ml: 1 }}
                >
                  Tap to configure
                </Typography>
              ) : null}
              <IconButton
                size="small"
                className="task-timing-group__expand-btn"
                tabIndex={-1}
              >
                {timingOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Collapse in={timingOpen} timeout={300}>
              <Grid
                container
                justifyContent="flex-start"
                alignItems="center"
                columnSpacing={{ xs: 0, sm: 4 }}
                rowSpacing={{ xs: 2, sm: 2 }}
                sx={{ pt: 1 }}
              >
                <Grid item xs={12} sm={6}>
                  <DeadlineContainer
                    deadline={deadline}
                    setHasDeadline={setHasDeadline}
                    hasDeadline={hasDeadline}
                    setDeadline={setDeadline}
                    isRecurring={isRecurring}
                    setRecurring={setRecurring}
                    setFrequency={setFrequency}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ScheduleContainer
                    schedule={schedule}
                    setSchedule={setSchedule}
                    deadline={deadline}
                    hasDeadline={hasDeadline}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <RecurringTasksContainer
                    frequency={frequency}
                    isRecurring={isRecurring}
                    setRecurring={setRecurring}
                    setFrequency={setFrequency}
                    hasDeadline={hasDeadline}
                    setHasDeadline={setHasDeadline}
                    setDeadline={setDeadline}
                  />
                </Grid>
              </Grid>
            </Collapse>
          </div>
          <NotesContainer
            onAddNote={onAddNote}
            notes={notes}
            onNoteStatusChange={onNoteStatusChange}
            onNoteDeleted={onNoteDeleted}
            noteInputValueRef={noteInputValueRef}
            noteInputError={noteInputError}
            onNoteInputErrorChange={onNoteInputErrorChange}
          />
        </Grid>
      </Grid>

      {/* Labels Dialog Modal - Only render when open */}
      {showEditLabelsPanel && (
        <LabelsDialog
          open={showEditLabelsPanel}
          onClose={() => onChangeEditLabelPanelVisibility(false)}
          selectedLabels={labels}
          onSelectedLabelsChange={onLabelsChange}
          onLabelRemoved={onLabelRemoved}
        />
      )}
    </>
  );
};

export default TaskDataContainer;
